import { UserRole } from '~/models';
import { KycCompletionStatus, PayoutBankAccountType } from '~/store/store.model';
import { makeDefaultBranding } from '../store.utils';
/** Returns the user's current business. */ const business = (s)=>s.userInfo ? s.userInfo.business() : undefined;
const videos = (s)=>s.videos;
/** Returns whether the user is a vendor. */ const isVendor = (s)=>!!s.userInfo && s.userInfo.isVendor();
/** Returns whether the user is a client. */ const isClient = (s)=>!!s.userInfo && s.userInfo.isCustomer();
/** Returns the role of the user in his current business. */ const role = (s)=>s.userInfo ? s.userInfo.role() : undefined;
const hasBusiness = (s)=>!!business(s);
const businessId = (s)=>{
    var _business;
    return (_business = business(s)) === null || _business === void 0 ? void 0 : _business.id;
};
const businessName = (s)=>{
    var _s_userInfo;
    return (_s_userInfo = s.userInfo) === null || _s_userInfo === void 0 ? void 0 : _s_userInfo.businessName();
};
const workspaces = (s)=>{
    var _s_userInfo;
    return (_s_userInfo = s.userInfo) === null || _s_userInfo === void 0 ? void 0 : _s_userInfo.workspaces();
};
const businessesWithAccountantAccess = (s)=>{
    if (s.accountantAccessBusinessIds != null) {
        return s.accountantAccessBusinessIds;
    }
    const spaces = workspaces(s);
    const val = spaces ? spaces.filter((ws)=>ws.isAccountant()).map((ws)=>ws.id()) : [];
    return new Set(val);
};
const businessSettings = (s)=>{
    var _business;
    return (_business = business(s)) === null || _business === void 0 ? void 0 : _business.settings;
};
const id = (s)=>{
    var _s_userInfo;
    return (_s_userInfo = s.userInfo) === null || _s_userInfo === void 0 ? void 0 : _s_userInfo.id();
};
const email = (s)=>{
    var _s_userInfo;
    return (_s_userInfo = s.userInfo) === null || _s_userInfo === void 0 ? void 0 : _s_userInfo.email();
};
const firstName = (s)=>{
    var _s_userInfo;
    return (_s_userInfo = s.userInfo) === null || _s_userInfo === void 0 ? void 0 : _s_userInfo.firstName();
};
const businessClientData = (s, businessID)=>{
    const bid = businessID || businessId(s);
    return bid ? s.businessClientData[bid] : undefined;
};
const isVendorInRelationship = (relationship)=>(s)=>{
        const bid = businessId(s);
        return !!bid && relationship.vendor.id === bid;
    };
/**
 * Returns true if the user has a role of 'member' in his current business.
 * Returns false if we don't have worksAt data.
 */ const isMemberUser = (state)=>{
    const userRole = role(state);
    return !!userRole && userRole === UserRole.Member;
};
const isAccountant = (s)=>{
    const userRole = role(s);
    return !!userRole && userRole === UserRole.Accountant;
};
const isAnchorAdmin = (s)=>{
    const userRole = role(s);
    return !!userRole && userRole === UserRole.AnchorAdmin;
};
const authProvider = (s)=>{
    var _s_userInfo;
    return (_s_userInfo = s.userInfo) === null || _s_userInfo === void 0 ? void 0 : _s_userInfo.oAuthProvider();
};
export const userSelectors = {
    isVendor,
    isClient,
    role,
    business,
    videos,
    workspaces,
    businessesWithAccountantAccess,
    businessSettings,
    hasBusiness,
    businessId,
    businessName,
    id,
    email,
    firstName,
    lastName: (s)=>{
        var _s_userInfo;
        return (_s_userInfo = s.userInfo) === null || _s_userInfo === void 0 ? void 0 : _s_userInfo.lastName();
    },
    businessClientData,
    modifiedAServiceTemplate: (s)=>{
        var _businessClientData;
        return !!((_businessClientData = businessClientData(s)) === null || _businessClientData === void 0 ? void 0 : _businessClientData.modifiedAServiceTemplate);
    },
    isVendorInRelationship,
    isMemberUser,
    isAccountant,
    isReviewer (s) {
        const userRole = role(s);
        return !!userRole && userRole === UserRole.Reviewer;
    },
    isAnchorAdmin,
    authProvider,
    kycCompleted (s) {
        const worksAt = business(s);
        return worksAt ? worksAt.kycCompletionStatus === KycCompletionStatus.Completed : false;
    },
    hasLinkedBankAccount (s) {
        const worksAt = business(s);
        return worksAt ? worksAt.hasLinkedBankAccount : false;
    },
    payoutBankAccount (s) {
        const worksAt = business(s);
        return worksAt ? worksAt.payoutBankAccount : null;
    },
    hasBankAccountConnected (s) {
        var _userSelectors_payoutBankAccount;
        return ((_userSelectors_payoutBankAccount = userSelectors.payoutBankAccount(s)) === null || _userSelectors_payoutBankAccount === void 0 ? void 0 : _userSelectors_payoutBankAccount.type) === PayoutBankAccountType.Connected;
    },
    hasDisabledPayments (s) {
        return !!s.userInfo && s.userInfo.disabledPayments();
    },
    hasPassword (s) {
        return !!s.userInfo && s.userInfo.hasPassword();
    },
    hasIdentity (s) {
        return !!s.userInfo && s.userInfo.hasIdentity();
    },
    hasUserInfo (s) {
        return s.userInfo != null;
    },
    branding: (s)=>{
        var _business;
        return ((_business = business(s)) === null || _business === void 0 ? void 0 : _business.branding) || makeDefaultBranding();
    },
    businessLogo: (s)=>{
        var _business;
        return ((_business = business(s)) === null || _business === void 0 ? void 0 : _business.logoUrl) || null;
    },
    invoiceLineItemsGrouped: (s)=>{
        var _business_settings, _business;
        return ((_business = business(s)) === null || _business === void 0 ? void 0 : (_business_settings = _business.settings) === null || _business_settings === void 0 ? void 0 : _business_settings.invoiceLineItems) === 'grouped';
    },
    activeTokenMatchesBusiness (s) {
        var _s_claims;
        const claimsBusinessId = (_s_claims = s.claims) === null || _s_claims === void 0 ? void 0 : _s_claims.businessId;
        const userInfoBusinessId = businessId(s);
        return claimsBusinessId != null && userInfoBusinessId != null && claimsBusinessId === userInfoBusinessId;
    },
    invoiceLineItemDescriptionEnabled: (s)=>{
        var _business_settings, _business;
        return ((_business = business(s)) === null || _business === void 0 ? void 0 : (_business_settings = _business.settings) === null || _business_settings === void 0 ? void 0 : _business_settings.invoiceLineItemDescription) === 'service';
    },
    invoiceLineItemPeriodEnabled: (s)=>{
        var _business_settings, _business;
        return ((_business = business(s)) === null || _business === void 0 ? void 0 : (_business_settings = _business.settings) === null || _business_settings === void 0 ? void 0 : _business_settings.invoiceLineItemPeriod) === 'show';
    },
    numSampleAgreements: (s)=>{
        var _s_userInfo_business, _s_userInfo;
        return (_s_userInfo = s.userInfo) === null || _s_userInfo === void 0 ? void 0 : (_s_userInfo_business = _s_userInfo.business()) === null || _s_userInfo_business === void 0 ? void 0 : _s_userInfo_business.numSampleAgreements;
    }
};
